import { useEffect, useState } from "react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line,
} from "recharts";
import { ChartPieSlice, ChartBar, TrendUp, FunnelSimple, FilePdf, FileXls, DownloadSimple } from "@phosphor-icons/react";
import { api, money, downloadFile } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const PALETTE = ["#115072", "#d6b729", "#bf5027", "#2f7ea3", "#6b7a86", "#8aa63f"];

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-card border border-border rounded-md p-5">
      <div className="flex items-center gap-2 mb-4"><Icon size={18} className="text-primary" /><span className="font-head font-semibold text-sm">{title}</span></div>
      {children}
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ program: "", funding_source: "", start: "", end: "" });

  const load = async () => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    const { data } = await api.get("/dashboard/analytics", { params });
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f]);

  if (!data) return null;
  const opts = data.options || { programs: [], funding_sources: [] };
  const active = Object.values(f).some(Boolean);

  const exportAs = async (format) => {
    const params = { format };
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    const ext = format === "xlsx" ? "xlsx" : format;
    await downloadFile("/dashboard/analytics/export", params, `accord_analytics.${ext}`);
  };

  return (
    <div className="mb-8">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div className="overline text-muted-foreground">Leadership Analytics</div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-40">
            <Label className="overline text-muted-foreground text-[10px]">Program</Label>
            <Select value={f.program || "all"} onValueChange={(v) => setF({ ...f, program: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="analytics-program" className="h-9"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All programs</SelectItem>{opts.programs.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="w-44">
            <Label className="overline text-muted-foreground text-[10px]">Funding source</Label>
            <Select value={f.funding_source || "all"} onValueChange={(v) => setF({ ...f, funding_source: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="analytics-source" className="h-9"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All sources</SelectItem>{opts.funding_sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="overline text-muted-foreground text-[10px]">From</Label><Input data-testid="analytics-start" type="date" className="h-9 w-36" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} /></div>
          <div><Label className="overline text-muted-foreground text-[10px]">To</Label><Input data-testid="analytics-end" type="date" className="h-9 w-36" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} /></div>
          {active && <Button variant="ghost" size="sm" data-testid="analytics-clear" onClick={() => setF({ program: "", funding_source: "", start: "", end: "" })}><FunnelSimple size={14} className="mr-1" /> Clear</Button>}
          <div className="flex items-center gap-1.5 border-l border-border pl-2 ml-1">
            <Button variant="outline" size="sm" data-testid="export-analytics-pdf" onClick={() => exportAs("pdf")}><FilePdf size={14} className="mr-1" /> PDF</Button>
            <Button variant="outline" size="sm" data-testid="export-analytics-xlsx" onClick={() => exportAs("xlsx")}><FileXls size={14} className="mr-1" /> Excel</Button>
            <Button variant="outline" size="sm" data-testid="export-analytics-csv" onClick={() => exportAs("csv")}><DownloadSimple size={14} className="mr-1" /> CSV</Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Funding by Source" icon={ChartPieSlice}>
          {data.funding_by_source.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.funding_by_source} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={80} paddingAngle={2}>
                  {data.funding_by_source.map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => money(v)} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Pipeline by Stage" icon={ChartBar}>
          {data.pipeline_by_stage.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.pipeline_by_stage} margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="stage" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} cursor={{ fill: "#11507210" }} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} fill="#115072" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Overdue Reviews Trend" icon={TrendUp}>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.overdue_trend} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="overdue" stroke="#bf5027" strokeWidth={2} dot={{ r: 3 }} name="Overdue" />
              <Line type="monotone" dataKey="in_review" stroke="#115072" strokeWidth={2} dot={{ r: 3 }} name="In Review" />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}

function Empty() {
  return <div className="h-[260px] flex items-center justify-center text-sm text-muted-foreground">No data for current filters</div>;
}
